// ===================================================================
// Time & Labor — FF-104 Summary Generator
// Generates FEMA Form FF-104 (Force Account Labor Summary) from
// time entry data. Preview + PDF + CSV export.
// ===================================================================

import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Title, Text, Paper, Table, Button, Group, TextInput, Select, Textarea,
  SimpleGrid, Badge, Stepper, MultiSelect, Divider, LoadingOverlay,
} from '../../components/gab/GabComponents';
import {
  IconDownload, IconArrowRight,
  IconArrowLeft, IconPrinter,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useTimeLaborStore, type LaborEmployee } from '../../store/timeLaborStore';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { useActivePADisaster as useActiveDisaster } from '../../hooks/useActiveDisaster';
import { useModulePermission } from '../../hooks/useModulePermission';
import { formatCurrency, stripCurrency } from '../../lib/formatUtils';
import { exportToCSV } from '../../lib/exportUtils';
import jsPDF from 'jspdf';

const TEAL = '#0D9488';

function pf(v: string | number | undefined): number {
  if (typeof v === 'number') return v || 0;
  return parseFloat(stripCurrency(v || '')) || 0;
}

function formatDateISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function formatDateShort(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function formatDateFull(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

/** Get all dates between from and to inclusive */
function getDateRange(from: string, to: string): string[] {
  const dates: string[] = [];
  const start = new Date(from + 'T12:00:00');
  const end = new Date(to + 'T12:00:00');
  const cur = new Date(start);
  while (cur <= end) {
    dates.push(formatDateISO(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

// FF-104 data structures
interface EmployeeSummary {
  employeeName: string;
  jobTitle: string;
  employeeId: string;
  regRate: number;
  otRate: number;
  benefitRatePerHr: number;
  dailyReg: Record<string, number>; // date -> hours
  dailyOT: Record<string, number>;
  totalRegHours: number;
  totalOTHours: number;
  totalRegCost: number;
  totalOTCost: number;
  accountCode: string;
  programCode: string;
}

export default function FF104Generator() {
  useModulePermission('timelabor');

  const { employees, fetchEmployees, laborRecords, loadingRecords, fetchLaborRecords } = useTimeLaborStore();
  const { projects, organizations, fetchProjects, fetchOrganizations, fetchDisasters } = usePAGrantsStore();
  const activeDisaster = useActiveDisaster();

  const [step, setStep] = useState(0);

  // Step 1: Configuration
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [filterCategory, setFilterCategory] = useState<string | null>(null);
  const [filterProjectId, setFilterProjectId] = useState<string | null>(null);
  const [applicantName, setApplicantName] = useState('');
  const [paId, setPaId] = useState('');
  const [projectNumber, setProjectNumber] = useState('');
  const [disasterName, setDisasterName] = useState('');
  const [locationSite, setLocationSite] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    fetchEmployees();
    fetchLaborRecords();
    fetchProjects();
    fetchOrganizations();
    fetchDisasters();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-fill from active disaster
  useEffect(() => {
    if (activeDisaster) {
      setDisasterName(`${activeDisaster.disasterNumber || ''} ${activeDisaster.disasterName || ''}`.trim());
    }
  }, [activeDisaster]);

  // Pre-fill applicant from first org
  useEffect(() => {
    if (organizations.length > 0 && !applicantName) {
      const grantee = organizations.find(o => o.orgRole === 'grantee');
      if (grantee) setApplicantName(grantee.orgName);
    }
  }, [organizations, applicantName]);

  // Employee multi-select options
  const employeeOptions = useMemo(() => {
    const names = new Set(laborRecords.map(r => r.employeeName).filter(Boolean));
    return [...names].sort().map(n => ({ value: String(n), label: String(n) }));
  }, [laborRecords]);

  // Category options from TASK_TYPES
  const categoryOptions = [
    { value: 'A', label: 'A - Debris Removal' },
    { value: 'B', label: 'B - Emergency Protective' },
    { value: 'C', label: 'C - Roads & Bridges' },
    { value: 'D', label: 'D - Water Control' },
    { value: 'E', label: 'E - Buildings & Equipment' },
    { value: 'F', label: 'F - Utilities' },
    { value: 'G', label: 'G - Parks & Recreation' },
  ];

  const projectOptions = useMemo(() =>
    projects
      .filter(p => p.Rid != null)
      .map(p => ({
        value: String(p.Rid),
        label: String(`${p.projectNumber ? p.projectNumber + ' - ' : ''}${p.projectTitle || 'Untitled'}`),
      })),
    [projects]
  );

  // Build employee profile lookup
  const profileByName = useMemo(() => {
    const m = new Map<string, LaborEmployee>();
    employees.forEach(e => {
      if (e.employeeName) m.set(e.employeeName, e);
    });
    return m;
  }, [employees]);

  // Filtered records for the summary
  const filteredRecords = useMemo(() => {
    let list = laborRecords;
    if (dateFrom) list = list.filter(r => r.workDate >= dateFrom);
    if (dateTo) list = list.filter(r => r.workDate <= dateTo);
    if (selectedEmployees.length > 0) list = list.filter(r => selectedEmployees.includes(r.employeeName));
    if (filterCategory) list = list.filter(r => r.category === filterCategory);
    if (filterProjectId) list = list.filter(r => r.projectId != null && String(r.projectId) === filterProjectId);
    return list;
  }, [laborRecords, dateFrom, dateTo, selectedEmployees, filterCategory, filterProjectId]);

  // Date columns for the period
  const dateColumns = useMemo(() => {
    if (!dateFrom || !dateTo) return [];
    return getDateRange(dateFrom, dateTo);
  }, [dateFrom, dateTo]);

  // Build employee summaries
  const employeeSummaries = useMemo<EmployeeSummary[]>(() => {
    const map = new Map<string, EmployeeSummary>();

    for (const rec of filteredRecords) {
      const name = rec.employeeName || 'Unknown';
      if (!map.has(name)) {
        const profile = profileByName.get(name);
        const regRate = pf(rec.rateRegular) || pf(profile?.regularRate);
        const otRate = pf(rec.rateOvertime) || pf(profile?.overtimeRate);
        const fringePct = pf(rec.fringeBenefitsPct) || pf(profile?.totalFringePct);
        const benefitRatePerHr = regRate * fringePct / 100;

        map.set(name, {
          employeeName: name,
          jobTitle: rec.employeeTitle || profile?.jobTitle || '',
          employeeId: rec.employeeId || profile?.employeeId || '',
          regRate,
          otRate,
          benefitRatePerHr,
          dailyReg: {},
          dailyOT: {},
          totalRegHours: 0,
          totalOTHours: 0,
          totalRegCost: 0,
          totalOTCost: 0,
          accountCode: rec.accountCode || '',
          programCode: rec.programCode || '',
        });
      }

      const summary = map.get(name)!;
      const regHrs = pf(rec.hoursRegular);
      const otHrs = pf(rec.hoursOvertime);
      const date = rec.workDate;

      if (date) {
        summary.dailyReg[date] = (summary.dailyReg[date] || 0) + regHrs;
        summary.dailyOT[date] = (summary.dailyOT[date] || 0) + otHrs;
      }
      summary.totalRegHours += regHrs;
      summary.totalOTHours += otHrs;
    }

    // Calculate costs
    for (const s of map.values()) {
      const totalRegRate = s.regRate + s.benefitRatePerHr;
      const totalOTRate = s.otRate + s.benefitRatePerHr;
      s.totalRegCost = s.totalRegHours * totalRegRate;
      s.totalOTCost = s.totalOTHours * totalOTRate;
    }

    return [...map.values()].sort((a, b) => (a.employeeName || '').localeCompare(b.employeeName || ''));
  }, [filteredRecords, profileByName]);

  // Grand totals
  const grandTotals = useMemo(() => {
    let regCost = 0, otCost = 0;
    for (const s of employeeSummaries) {
      regCost += s.totalRegCost;
      otCost += s.totalOTCost;
    }
    return { regCost, otCost, total: regCost + otCost };
  }, [employeeSummaries]);

  // Limit date columns for PDF: max ~14 columns (2 weeks)
  const maxDateCols = 14;
  const displayDates = useMemo(() => dateColumns.slice(0, maxDateCols), [dateColumns]);

  // ── CSV Export ──────────────────────────────────────────────────
  const handleCSVExport = useCallback(() => {
    const headers = ['Employee', 'Job Title', 'Type', ...displayDates.map(d => formatDateShort(d)),
      'Total Hours', 'Hourly Rate', 'Benefit Rate/HR', 'Total Hourly Rate', 'Total Cost', 'Account Code', 'Program Code'];

    const rows: string[][] = [];
    for (const emp of employeeSummaries) {
      // REG row
      const totalRegRate = emp.regRate + emp.benefitRatePerHr;
      rows.push([
        emp.employeeName, emp.jobTitle, 'REG',
        ...displayDates.map(d => (emp.dailyReg[d] || 0).toFixed(1)),
        emp.totalRegHours.toFixed(1),
        emp.regRate.toFixed(2),
        emp.benefitRatePerHr.toFixed(2),
        totalRegRate.toFixed(2),
        emp.totalRegCost.toFixed(2),
        emp.accountCode,
        emp.programCode,
      ]);
      // OT row
      const totalOTRate = emp.otRate + emp.benefitRatePerHr;
      rows.push([
        '', '', 'O.T.',
        ...displayDates.map(d => (emp.dailyOT[d] || 0).toFixed(1)),
        emp.totalOTHours.toFixed(1),
        emp.otRate.toFixed(2),
        emp.benefitRatePerHr.toFixed(2),
        totalOTRate.toFixed(2),
        emp.totalOTCost.toFixed(2),
        '',
        '',
      ]);
    }
    // Totals
    rows.push([]);
    rows.push(['', '', '', ...displayDates.map(() => ''), '', '', '', 'Regular Time Total', grandTotals.regCost.toFixed(2)]);
    rows.push(['', '', '', ...displayDates.map(() => ''), '', '', '', 'Overtime Total', grandTotals.otCost.toFixed(2)]);
    rows.push(['', '', '', ...displayDates.map(() => ''), '', '', '', 'GRAND TOTAL', grandTotals.total.toFixed(2)]);

    exportToCSV('ff104-force-account-labor', headers, rows);
    notifications.show({ title: 'Exported', message: 'FF-104 CSV downloaded', color: TEAL });
  }, [employeeSummaries, displayDates, grandTotals]);

  // ── PDF Generation ─────────────────────────────────────────────
  const handleGeneratePDF = useCallback(() => {
    if (employeeSummaries.length === 0) {
      notifications.show({ title: 'No Data', message: 'No labor records match your filters', color: 'red' });
      return;
    }

    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 36;

    const EMPLOYEES_PER_PAGE = 4;
    const pages = Math.ceil(employeeSummaries.length / EMPLOYEES_PER_PAGE);

    for (let page = 0; page < pages; page++) {
      if (page > 0) doc.addPage();

      let y = margin;

      // Header
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.text('DEPARTMENT OF HOMELAND SECURITY', pageW / 2, y, { align: 'center' });
      y += 10;
      doc.text('Federal Emergency Management Agency', pageW / 2, y, { align: 'center' });
      y += 12;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('FORCE ACCOUNT LABOR SUMMARY', pageW / 2, y, { align: 'center' });
      y += 16;

      // Header fields — two columns
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      const col1 = margin;
      const col2 = pageW / 2 + 20;
      const fieldH = 12;

      const headerFields = [
        [{ l: 'APPLICANT', v: applicantName }, { l: 'PA ID #', v: paId }],
        [{ l: 'PROJECT #', v: projectNumber }, { l: 'DISASTER', v: disasterName }],
        [{ l: 'LOCATION/SITE', v: locationSite }, { l: 'CATEGORY', v: category }],
        [{ l: 'PERIOD COVERING', v: dateFrom && dateTo ? `${formatDateFull(dateFrom)} - ${formatDateFull(dateTo)}` : '' }, { l: 'ACCOUNT / PROGRAM', v: employeeSummaries.length > 0 ? [employeeSummaries[0].accountCode, employeeSummaries[0].programCode].filter(Boolean).join(' / ') : '' }],
      ];

      for (const row of headerFields) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6);
        doc.text(row[0].l, col1, y);
        if (row[1].l) doc.text(row[1].l, col2, y);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.text(row[0].v, col1 + 60, y);
        if (row[1].v) doc.text(row[1].v, col2 + 40, y);
        y += fieldH;
      }

      // Description
      if (description) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6);
        doc.text('DESCRIPTION OF WORK PERFORMED', col1, y);
        doc.setFontSize(7);
        const descLines = doc.splitTextToSize(description, pageW - margin * 2);
        y += 8;
        doc.text(descLines.slice(0, 2), col1, y);
        y += Math.min(descLines.length, 2) * 8;
      }

      y += 6;

      // Employee grid
      const nameColW = 100;
      const titleColW = 70;
      const dateColW = Math.min(32, (pageW - margin * 2 - nameColW - titleColW - 160) / Math.max(displayDates.length, 1));
      const totalHrsW = 36;
      const rateW = 36;
      const benefitW = 36;
      const totalRateW = 38;

      // Column headers
      doc.setFontSize(6);
      doc.setFont('helvetica', 'bold');
      let x = margin;
      doc.text('NAME / JOB TITLE', x, y);
      x = margin + nameColW + titleColW;

      // Date headers
      for (const d of displayDates) {
        doc.text(formatDateShort(d), x, y, { align: 'center' });
        x += dateColW;
      }

      const afterDates = x;
      doc.text('TOTAL', afterDates, y, { align: 'center' });
      doc.text('HOURLY', afterDates + totalHrsW, y, { align: 'center' });
      doc.text('BENEFIT', afterDates + totalHrsW + rateW, y, { align: 'center' });
      doc.text('TOTAL', afterDates + totalHrsW + rateW + benefitW, y, { align: 'center' });
      doc.text('TOTAL', afterDates + totalHrsW + rateW + benefitW + totalRateW, y, { align: 'center' });
      y += 7;
      doc.text('', margin, y);
      x = afterDates;
      doc.text('HRS', x, y, { align: 'center' });
      doc.text('RATE', x + totalHrsW, y, { align: 'center' });
      doc.text('RATE/HR', x + totalHrsW + rateW, y, { align: 'center' });
      doc.text('HR RATE', x + totalHrsW + rateW + benefitW, y, { align: 'center' });
      doc.text('COSTS', x + totalHrsW + rateW + benefitW + totalRateW, y, { align: 'center' });
      y += 8;

      // Draw line
      doc.setDrawColor(0);
      doc.setLineWidth(0.5);
      doc.line(margin, y, pageW - margin, y);
      y += 4;

      // Employees for this page
      const pageEmps = employeeSummaries.slice(page * EMPLOYEES_PER_PAGE, (page + 1) * EMPLOYEES_PER_PAGE);

      for (const emp of pageEmps) {
        // REG row
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.text(emp.employeeName.substring(0, 18), margin, y);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6);
        doc.text('REG', margin + nameColW - 20, y);
        doc.text(emp.jobTitle.substring(0, 14), margin + nameColW, y);

        x = margin + nameColW + titleColW;
        for (const d of displayDates) {
          const hrs = emp.dailyReg[d] || 0;
          if (hrs > 0) doc.text(hrs.toFixed(1), x, y, { align: 'center' });
          x += dateColW;
        }

        const totalRegRate = emp.regRate + emp.benefitRatePerHr;
        doc.text(emp.totalRegHours.toFixed(1), afterDates, y, { align: 'center' });
        doc.text(emp.regRate.toFixed(2), afterDates + totalHrsW, y, { align: 'center' });
        doc.text(emp.benefitRatePerHr.toFixed(2), afterDates + totalHrsW + rateW, y, { align: 'center' });
        doc.text(totalRegRate.toFixed(2), afterDates + totalHrsW + rateW + benefitW, y, { align: 'center' });
        doc.text(formatCurrency(emp.totalRegCost.toFixed(2)), afterDates + totalHrsW + rateW + benefitW + totalRateW, y, { align: 'right' });
        y += 10;

        // OT row
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6);
        doc.text('O.T.', margin + nameColW - 20, y);

        x = margin + nameColW + titleColW;
        for (const d of displayDates) {
          const hrs = emp.dailyOT[d] || 0;
          if (hrs > 0) doc.text(hrs.toFixed(1), x, y, { align: 'center' });
          x += dateColW;
        }

        const totalOTRate = emp.otRate + emp.benefitRatePerHr;
        doc.text(emp.totalOTHours.toFixed(1), afterDates, y, { align: 'center' });
        doc.text(emp.otRate.toFixed(2), afterDates + totalHrsW, y, { align: 'center' });
        doc.text(emp.benefitRatePerHr.toFixed(2), afterDates + totalHrsW + rateW, y, { align: 'center' });
        doc.text(totalOTRate.toFixed(2), afterDates + totalHrsW + rateW + benefitW, y, { align: 'center' });
        doc.text(formatCurrency(emp.totalOTCost.toFixed(2)), afterDates + totalHrsW + rateW + benefitW + totalRateW, y, { align: 'right' });
        y += 14;

        // Separator
        doc.setDrawColor(200);
        doc.setLineWidth(0.3);
        doc.line(margin, y - 4, pageW - margin, y - 4);
      }

      // Totals section
      y += 6;
      doc.setDrawColor(0);
      doc.setLineWidth(0.5);
      doc.line(margin, y, pageW - margin, y);
      y += 12;

      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      // Totals layout: label right-aligned at labelRightX, value right-aligned at pageW - margin
      // This prevents label text from running into the value column
      const valueRightX = pageW - margin;          // 756pt — right-align values here
      const labelRightX = valueRightX - 110;        // 646pt — right-align labels here (110pt gap)
      doc.text('Regular Time Labor Cost:', labelRightX, y, { align: 'right' });
      doc.text(formatCurrency(grandTotals.regCost.toFixed(2)), valueRightX, y, { align: 'right' });
      y += 12;
      doc.text('Overtime Labor Cost:', labelRightX, y, { align: 'right' });
      doc.text(formatCurrency(grandTotals.otCost.toFixed(2)), valueRightX, y, { align: 'right' });
      y += 12;
      doc.setLineWidth(1);
      doc.line(valueRightX - 110, y - 2, valueRightX, y - 2);
      doc.text('GRAND TOTAL:', labelRightX, y + 2, { align: 'right' });
      doc.text(formatCurrency(grandTotals.total.toFixed(2)), valueRightX, y + 2, { align: 'right' });
      y += 20;

      // Certification
      doc.setFontSize(6);
      doc.setFont('helvetica', 'normal');
      doc.text(
        'I CERTIFY THAT THE INFORMATION ABOVE WAS OBTAINED FROM PAYROLL RECORDS, INVOICES, OR OTHER DOCUMENTS THAT ARE AVAILABLE FOR AUDIT.',
        margin, y
      );
      y += 14;

      // Signature block
      doc.setFontSize(7);
      doc.text('CERTIFIED: ____________________________', margin, y);
      doc.text('TITLE: ____________________________', margin + 250, y);
      doc.text('DATE: ____________', margin + 500, y);

      // Form footer
      doc.setFontSize(5);
      doc.setFont('helvetica', 'normal');
      doc.text('FEMA Form FF-104-FY-21-137 (formerly 009-0-123)', margin, pageH - 20);
      doc.text(`Page ${page + 1} of ${pages}`, pageW - margin, pageH - 20, { align: 'right' });
    }

    doc.save('FF-104-Force-Account-Labor-Summary.pdf');
    notifications.show({ title: 'PDF Generated', message: 'FF-104 PDF downloaded', color: TEAL });
  }, [employeeSummaries, displayDates, grandTotals, applicantName, paId, projectNumber, disasterName, locationSite, category, description, dateFrom, dateTo]);

  // ── Validation ──────────────────────────────────────────────────
  const canProceedToPreview = dateFrom && dateTo && filteredRecords.length > 0;

  return (
    <div style={{ position: 'relative' }}>
      <LoadingOverlay visible={loadingRecords && laborRecords.length === 0} />

      <Group justify="space-between" mb="md">
        <div>
          <Title order={3}>FF-104 Generator</Title>
          <Text size="sm" c="dimmed">Generate FEMA Form FF-104 — Force Account Labor Summary</Text>
        </div>
      </Group>

      <Stepper active={step} onStepClick={setStep} mb="lg" color={TEAL} size="sm">
        <Stepper.Step label="Configure" description="Set period & filters">
          <Paper p="md" withBorder style={{ borderTop: `3px solid ${TEAL}` }}>
            <Title order={5} mb="sm">Period & Filters</Title>
            <SimpleGrid cols={{ base: 1, sm: 2 }} mb="md">
              <TextInput
                label="Period From"
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                required
              />
              <TextInput
                label="Period To"
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                required
              />
            </SimpleGrid>

            <MultiSelect
              label="Employees (leave blank for all)"
              data={employeeOptions}
              value={selectedEmployees}
              onChange={setSelectedEmployees}
              searchable
              clearable
              mb="sm"
              placeholder="All employees"
            />

            <SimpleGrid cols={{ base: 1, sm: 2 }} mb="md">
              <Select
                label="Filter by Category"
                data={categoryOptions}
                value={filterCategory}
                onChange={setFilterCategory}
                clearable
                placeholder="All categories"
              />
              <Select
                label="Filter by PA Project"
                data={projectOptions}
                value={filterProjectId}
                onChange={setFilterProjectId}
                clearable
                searchable
                placeholder="All projects"
              />
            </SimpleGrid>

            <Divider my="md" label="Form Header Fields" labelPosition="left" />

            <SimpleGrid cols={{ base: 1, sm: 2 }} mb="sm">
              <TextInput label="Applicant" value={applicantName} onChange={e => setApplicantName(e.target.value)} />
              <TextInput label="PA ID #" value={paId} onChange={e => setPaId(e.target.value)} />
            </SimpleGrid>
            <SimpleGrid cols={{ base: 1, sm: 3 }} mb="sm">
              <TextInput label="Project #" value={projectNumber} onChange={e => setProjectNumber(e.target.value)} />
              <TextInput label="Disaster" value={disasterName} onChange={e => setDisasterName(e.target.value)} />
              <TextInput label="Category" value={category} onChange={e => setCategory(e.target.value)} />
            </SimpleGrid>
            <TextInput label="Location / Site" value={locationSite} onChange={e => setLocationSite(e.target.value)} mb="sm" />
            <Textarea label="Description of Work Performed" value={description} onChange={e => setDescription(e.target.value)} rows={2} mb="md" />

            <Group justify="space-between">
              <Badge size="lg" variant="light" color={filteredRecords.length > 0 ? TEAL : 'gray'}>
                {filteredRecords.length} matching records / {employeeSummaries.length} employees
              </Badge>
              <Button
                color={TEAL}
                rightSection={<IconArrowRight size={14} />}
                onClick={() => setStep(1)}
                disabled={!canProceedToPreview}
              >
                Preview
              </Button>
            </Group>
          </Paper>
        </Stepper.Step>

        <Stepper.Step label="Preview" description="Review FF-104 table">
          <Paper p="md" withBorder style={{ borderTop: `3px solid ${TEAL}` }}>
            {/* Header info */}
            <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md">
              {applicantName && <div><Text size="xs" c="dimmed">Applicant</Text><Text size="sm" fw={500}>{applicantName}</Text></div>}
              {paId && <div><Text size="xs" c="dimmed">PA ID #</Text><Text size="sm" fw={500}>{paId}</Text></div>}
              {projectNumber && <div><Text size="xs" c="dimmed">Project #</Text><Text size="sm" fw={500}>{projectNumber}</Text></div>}
              {disasterName && <div><Text size="xs" c="dimmed">Disaster</Text><Text size="sm" fw={500}>{disasterName}</Text></div>}
            </SimpleGrid>

            {dateFrom && dateTo && (
              <Text size="sm" mb="md" c="dimmed">
                Period: {formatDateFull(dateFrom)} - {formatDateFull(dateTo)}
                {dateColumns.length > maxDateCols && ` (showing first ${maxDateCols} of ${dateColumns.length} days)`}
              </Text>
            )}

            {/* FF-104 Grid */}
            <div style={{ overflowX: 'auto' }}>
              <Table striped highlightOnHover withTableBorder withColumnBorders style={{ fontSize: 11 }}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ minWidth: 120 }}>NAME / JOB TITLE</Table.Th>
                    <Table.Th style={{ width: 40 }}></Table.Th>
                    {displayDates.map(d => (
                      <Table.Th key={d} style={{ textAlign: 'center', minWidth: 40, fontSize: 10 }}>{formatDateShort(d)}</Table.Th>
                    ))}
                    <Table.Th style={{ textAlign: 'right', minWidth: 50 }}>TOTAL HRS</Table.Th>
                    <Table.Th style={{ textAlign: 'right', minWidth: 55 }}>HOURLY RATE</Table.Th>
                    <Table.Th style={{ textAlign: 'right', minWidth: 55 }}>BENEFIT RATE/HR</Table.Th>
                    <Table.Th style={{ textAlign: 'right', minWidth: 60 }}>TOTAL HR RATE</Table.Th>
                    <Table.Th style={{ textAlign: 'right', minWidth: 70 }}>TOTAL COSTS</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {employeeSummaries.length === 0 ? (
                    <Table.Tr>
                      <Table.Td colSpan={5 + displayDates.length}>
                        <Text size="sm" c="dimmed" ta="center" py="lg">No records match the selected filters.</Text>
                      </Table.Td>
                    </Table.Tr>
                  ) : employeeSummaries.map(emp => {
                    const totalRegRate = emp.regRate + emp.benefitRatePerHr;
                    const totalOTRate = emp.otRate + emp.benefitRatePerHr;
                    return [
                      // REG row
                      <Table.Tr key={emp.employeeName + '-reg'}>
                        <Table.Td fw={600} style={{ fontSize: 11 }}>
                          {emp.employeeName}
                          <Text size="xs" c="dimmed">{emp.jobTitle}</Text>
                        </Table.Td>
                        <Table.Td><Badge size="xs" variant="light" color="blue">REG</Badge></Table.Td>
                        {displayDates.map(d => (
                          <Table.Td key={d} style={{ textAlign: 'center' }}>
                            {(emp.dailyReg[d] || 0) > 0 ? (emp.dailyReg[d]).toFixed(1) : ''}
                          </Table.Td>
                        ))}
                        <Table.Td style={{ textAlign: 'right' }} fw={600}>{emp.totalRegHours.toFixed(1)}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>${emp.regRate.toFixed(2)}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>${emp.benefitRatePerHr.toFixed(2)}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>${totalRegRate.toFixed(2)}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }} fw={700}>{formatCurrency(emp.totalRegCost.toFixed(2))}</Table.Td>
                      </Table.Tr>,
                      // OT row
                      <Table.Tr key={emp.employeeName + '-ot'} style={{ background: '#FFFBEB' }}>
                        <Table.Td></Table.Td>
                        <Table.Td><Badge size="xs" variant="light" color="orange">O.T.</Badge></Table.Td>
                        {displayDates.map(d => (
                          <Table.Td key={d} style={{ textAlign: 'center' }}>
                            {(emp.dailyOT[d] || 0) > 0 ? (emp.dailyOT[d]).toFixed(1) : ''}
                          </Table.Td>
                        ))}
                        <Table.Td style={{ textAlign: 'right' }} fw={600}>{emp.totalOTHours.toFixed(1)}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>${emp.otRate.toFixed(2)}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>${emp.benefitRatePerHr.toFixed(2)}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>${totalOTRate.toFixed(2)}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }} fw={700}>{formatCurrency(emp.totalOTCost.toFixed(2))}</Table.Td>
                      </Table.Tr>,
                    ];
                  })}
                  {/* Totals */}
                  {employeeSummaries.length > 0 && (
                    <>
                      <Table.Tr style={{ background: '#F1F5F9' }}>
                        <Table.Td colSpan={2 + displayDates.length + 3}></Table.Td>
                        <Table.Td style={{ textAlign: 'right' }} fw={700}>Regular Time Total:</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }} fw={700}>{formatCurrency(grandTotals.regCost.toFixed(2))}</Table.Td>
                      </Table.Tr>
                      <Table.Tr style={{ background: '#FEF3C7' }}>
                        <Table.Td colSpan={2 + displayDates.length + 3}></Table.Td>
                        <Table.Td style={{ textAlign: 'right' }} fw={700}>Overtime Total:</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }} fw={700}>{formatCurrency(grandTotals.otCost.toFixed(2))}</Table.Td>
                      </Table.Tr>
                      <Table.Tr style={{ background: '#ECFDF5' }}>
                        <Table.Td colSpan={2 + displayDates.length + 3}></Table.Td>
                        <Table.Td style={{ textAlign: 'right' }} fw={700}>GRAND TOTAL:</Table.Td>
                        <Table.Td style={{ textAlign: 'right', fontSize: 14 }} fw={700} c={TEAL}>{formatCurrency(grandTotals.total.toFixed(2))}</Table.Td>
                      </Table.Tr>
                    </>
                  )}
                </Table.Tbody>
              </Table>
            </div>

            {/* Certification text */}
            <Paper p="xs" withBorder bg="gray.0" mt="md">
              <Text size="xs" c="dimmed" fs="italic">
                I CERTIFY THAT THE INFORMATION ABOVE WAS OBTAINED FROM PAYROLL RECORDS, INVOICES, OR OTHER DOCUMENTS THAT ARE AVAILABLE FOR AUDIT.
              </Text>
            </Paper>

            <Group justify="space-between" mt="md">
              <Button variant="light" color="gray" leftSection={<IconArrowLeft size={14} />} onClick={() => setStep(0)}>
                Back
              </Button>
              <Group gap={8}>
                <Button variant="light" color="gray" leftSection={<IconDownload size={14} />} onClick={handleCSVExport}>
                  Export CSV
                </Button>
                <Button color={TEAL} leftSection={<IconPrinter size={14} />} onClick={handleGeneratePDF}>
                  Generate FF-104 PDF
                </Button>
              </Group>
            </Group>
          </Paper>
        </Stepper.Step>
      </Stepper>
    </div>
  );
}
